import React, {useState} from "react";
import {Cell} from "./Cell";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import FilterListIcon from "@mui/icons-material/FilterList";
import {DESCENDING, UNSORTED, ASCENDING, DATE} from "../constants";
import {DATE_LINK, NUMBER, NUMBER_LINK, STRING, STRING_LINK, CURRENCY, CURRENCY_LINK, PERCENT} from "../constants";
import {ColumnHeader} from "../ColumnHeader";


export class ColumnDef {

    static columnDefsById = new Map();

    constructor({displayName, options = null, index = null, type = STRING, renderOrder=null, style=null}) {
        this.displayName = displayName;
        this.options = options; // ColumnDefOptions instance
        this._index = index;
        this.type = type;
        this._renderOrder = renderOrder === null ? index : renderOrder;
        this.style = style; // Optional style for the column
        this.isLink = this.type === STRING_LINK || this.type === NUMBER_LINK || this.type === DATE_LINK || this.type === CURRENCY_LINK;
        this.isNumber = this.type === NUMBER || this.type === CURRENCY || this.type === NUMBER_LINK || this.type === CURRENCY_LINK || this.type === PERCENT;
        this.isDate = this.type === DATE || this.type === DATE_LINK;
        this.isCategorical = this.type === STRING || this.type === STRING_LINK;
        this._changeSet = [];
        this._isHidden = false; // Default visibility state
    }

    static fromJSON(json) {
        if (json.id && ColumnDef.columnDefsById.has(json.id)) {
            return ColumnDef.columnDefsById.get(json.id);
        }
        const columnDef = new ColumnDef({
            displayName: json.displayName,
            options: json.options,
            index: json.index,
            type: json.type,
            renderOrder: json.renderOrder,
            style: json.style,
        });
        if (json.id) {
            ColumnDef.columnDefsById.set(json.id, columnDef);
        }
        return columnDef;
    }

    toJSON() {
        return {
            displayName: this.displayName,
            className: this.constructor.name,
            id: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15), // Unique ID for this
            options: this.options,
            index: this._index,
            type: this.type,
            renderOrder: this._renderOrder,
            style: this.style,
        };
    }

    isHidden() {
        return this._isHidden; // Check if the column is hidden
    }

    hide() {
        this._isHidden = true; // Set the visibility state of the column
    }

    show() {
        this._isHidden = false; // Reset the visibility state of the column
    }

    rollback() {
        this.setChangeSet(null); // Reset the change set to null
    }

    setChangeSet(changeSet) {
        this._changeSet = changeSet; // Set the change set for the column
    }

    getChangeSet() {
        return this._changeSet || [];
    }

    setRenderOrder(index) {
        if (index === null) {
            this._renderOrder = this.getIndex(); // Reset to index if null
            return;
        }
        this._renderOrder = index;
    }

    getRenderOrder() {
        if (this._renderOrder !== null) {
            return this._renderOrder;
        }
        return this.getIndex(); // Fallback to index if renderOrder is not set
    }

    setIndex(index) {
        if (this._index !== null) {
            console.error("Attempted to set index on an already defined column. This is not allowed:", this, "index:", index);
            throw new Error("Indexes are immutable!");
        }
        this._index = index; // Set the index of the column
        // this._state.sort.sortOrder = index; // Set the default sort order based on the index
    }

    getIndex() {
        return this._index; // Get the index of the column
    }

    key() {
        return `canold-table-column-${this.getIndex()}-${this.displayName}`;
    }

    getSortIcon(sortState) {
        if (sortState.direction === ASCENDING) {
            return <ArrowUpwardIcon className="canold-table-sort-icon-up clickable" role={"canold-table-sort-icon"}/>;
        } else if (sortState.direction === DESCENDING) {
            return <ArrowDownwardIcon className="canold-table-sort-icon-down clickable" role={"canold-table-sort-icon"}/>;
        }
        return <SwapVertIcon className={"canold-table-sort-icon-swapvert clickable"} role={"canold-table-sort-icon"}/>;
    }

    getFilterIcon(onFilterClick, hideModal, setHideModal) {
        return <span className={"canold-table-filter-icon"}>
            <FilterListIcon role={"canold-table-filter-icon"} className={"canold-table-filter-icon clickable"} onClick={(evt) => {
                evt.stopPropagation(); // Prevent event bubbling
                onFilterClick(this, evt);
                setHideModal(!hideModal);
            }}/>
        </span>;
    }

    isSortable() {
        return !this.options || this.options.sortable;
    }

    setStyle(style) {
        this.style = {...(this.style || {}), ...style}; // Merge with existing styles
    }

    getSentinelCellValue() {
        if (this.type === CURRENCY) {
            return 0.0; // Sentinel value for currency
        } else if (this.type === CURRENCY_LINK) {
            return {props: {href: "#", role: "link"}, displayValue: 0}; // Sentinel value for currency link
        } else if (this.type === NUMBER) {
            return 0.0; // Sentinel value for number
        } else if (this.type === NUMBER_LINK) {
            return {props: {href: "#", role: "link"}, displayValue: 0}; // Sentinel value for number link
        } else if (this.type === DATE) {
            return new Date(0).toLocaleDateString(); // Sentinel value for date (epoch start)
        } else if (this.type === DATE_LINK) {
            return {props: {href: "#", role: "link"}, displayValue: new Date()}; // Sentinel value for number link
        } else if (this.type === STRING) {
            return ""; // Sentinel value for string
        }  else if (this.type === STRING_LINK) {
            return {props: {href: "#", role: "link"}, displayValue: ""}; // Sentinel value for string link
        } else if (this.type === PERCENT) {
            return 0.0; // Sentinel value for percent
        }
    }

    getSentinelCell() {
        return new Cell(this, this.getSentinelCellValue(), 0); // Create a sentinel cell with the sentinel value
    }

    render({
        sortStack,
        onSortClick,
        onFilterChange,
        onFilterClick,
        isVisible,
        collapsed = false,
        setCollapsed = null,
    }) {
        return <ColumnHeader
            collapsed={collapsed}
            columnDef={this}
            isVisible={isVisible}
            key={this.key()}
            onFilterChange={onFilterChange}
            onFilterClick={onFilterClick}
            onSortClick={onSortClick}
            setCollapsed={setCollapsed}
            sortStack={sortStack}
        />
    }

}
